'use client'
import { faInstagram } from '@fortawesome/free-brands-svg-icons'
import { faCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { zodResolver } from '@hookform/resolvers/zod'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { useForm } from 'react-hook-form'
import Swal from 'sweetalert2/dist/sweetalert2.js'
import Button from '@/components/Form/Button'
import { useNewsletterSubscribe } from '@/hooks/business/pages/useNewsletterSubscribe'
import { INewsletterSubscribeForm, NewsletterSubscribeFormSchema } from '@/schemas/pages'
import LogoCommcepta from '../../../../public/assets/images/logo_commcepta.svg'

/** Rodapé da loja: inscrição na newsletter, links das páginas institucionais e da ajuda, endereço e créditos. */
export const Footer: React.FC = () => {
    const { subscribe, isPending } = useNewsletterSubscribe()
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<INewsletterSubscribeForm>({
        resolver: zodResolver(NewsletterSubscribeFormSchema),
    })

    const handleSubscribe = async (data: INewsletterSubscribeForm) => {
        try {
            await subscribe(data)
        } catch {
            return // o erro da API já saiu no toast do tratador padrão
        }
        Swal.fire({
            title: 'Sucesso!',
            text: 'Seu email foi cadastrado com sucesso!',
            icon: 'success',
            confirmButtonText: 'OK',
        })
    }

    return (
        <div className="w-full grid grid-row bg-primary h-min-[350px] z-0 absolute max-md:h-fit mt-10 max-sm:mt-0 pb-8">
            <div className="mx-auto max-md:ml-6 flex flex-row max-md:flex-col items-center w-[90%] sm:w-[650px] md:w-[800px] lg:w-[1000px] justify-between gap-4 bg-cta-yellow p-4 border-[1.5px]  border-dark-yellow rounded-full max-md:rounded-3xl md:top-0 -top-[250px] z-40 -mt-[30px]">
                <Image
                    src="/assets/icons/glofi_hand02.svg"
                    height={29}
                    width={60}
                    alt=""
                    className="ml-8 max-md:absolute max-md:-left-6 "
                />
                <h3 className="text-sm font-bold text-center text-primary max-md:text-base font-sora max-md:mx-8">
                    fique por dentro das nossas novidades e{' '}
                    <span className="font-eldwin-script" style={{ fontSize: '18px' }}>
                        promoções
                    </span>
                </h3>
                <FontAwesomeIcon icon={faCircle} color="#FFD18D" className="w-[10px]" />
                <input
                    placeholder="insira seu email aqui"
                    className={`bg-transparent text-sm max-md:text-center border-b w-[200px] focus:outline-hidden text-harmonic-blue ${errors.email ? 'border-jolly-rose focus:jolly-rose' : 'border-gray-600 placeholder-gray-600'}`}
                    {...register('email')}
                />

                <Button
                    size="md"
                    color="tertiary"
                    isLoading={isPending}
                    onClick={() => handleSubmit(handleSubscribe)()}
                >
                    enviar
                </Button>
            </div>
            <div className="flex justify-center w-full pt-24">
                <div className="container relative">
                    <div className="flex flex-row items-start w-full h-full mx-auto max-md:flex-col-reverse max-md:gap-3">
                        <div className="w-full basis-1/4 lg:-mt-3 md:-mt-3">
                            <div className="flex justify-center">
                                <div className="flex flex-col justify-start gap-2 md:flex-col-reverse">
                                    <a
                                        href="https://transparencyreport.google.com/safe-browsing/search?url=https://glofi.com.br/&hl=pt_BR"
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        <Image
                                            src="/images/google-navegacao-segura.png"
                                            height={100}
                                            width={200}
                                            alt=""
                                            className="-ml-4"
                                        />
                                    </a>
                                    <div className="flex gap-2">
                                        <a
                                            href="https://www.instagram.com/glofistore/"
                                            target="_blank"
                                            className="rounded-full bg-orange w-[34px] h-[34px] inline-flex justify-center items-center mb-6"
                                            rel="noopener"
                                        >
                                            <FontAwesomeIcon icon={faInstagram} color="#fff" />
                                        </a>

                                        <a
                                            href="https://www.instagram.com/glofistore/"
                                            target="_blank"
                                            className="h-[34px] w-[110px] flex items-center justify-center text-white bg-secondary rounded-full px-2"
                                            rel="noopener"
                                        >
                                            @glofistore
                                        </a>
                                    </div>

                                    <p className="pt-2 mb-6 text-sm text-gray-400">
                                        48.290.920/0002-07
                                        <br />
                                        Rua Santa Catarina, 225
                                        <br />
                                        Água Verde - Curitiba, PR
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center w-full basis-1/4 lg:-mt-3 md:-mt-3">
                            <ul className="list-none">
                                <li className="my-2 list-item ">
                                    <Link href="/p/quem-somos" className="text-sm font-bold text-light-blue">
                                        sobre nós
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    <Link
                                        href="/p/politica-de-privacidade"
                                        className="text-sm text-light-blue-inactive "
                                    >
                                        política de privacidade
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    <Link href="/p/termos-e-condicoes" className="text-sm text-light-blue-inactive ">
                                        termos e condições
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    <Link
                                        href="/p/declaracao-de-confianca"
                                        className="text-sm text-light-blue-inactive "
                                    >
                                        declaração de confiança
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    <Link href="/sustentabilidade" className="text-sm text-light-blue-inactive ">
                                        sustentabilidade
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    {' '}
                                    <a
                                        href="https://blog.glofi.com.br/"
                                        target="_blank"
                                        className="text-sm text-light-blue-inactive "
                                        rel="noopener"
                                    >
                                        glöb
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div className="flex justify-center w-full basis-1/4 lg:-mt-3 md:-mt-3">
                            <ul className="list-none w-[172px]">
                                <li className="my-2 list-item ">
                                    <Link href="/ajuda" className="text-sm font-bold text-light-blue">
                                        ajuda
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    <Link href="/p/faq" className="text-sm text-light-blue-inactive ">
                                        FAQ
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    <Link href="/ajuda" className="text-sm text-light-blue-inactive ">
                                        garantia glöfi
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    {' '}
                                    <Link href="/p/envio-e-pagamento" className="text-sm text-light-blue-inactive ">
                                        envio e pagamento
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    {' '}
                                    <Link href="/p/trocas-e-devolucoes" className="text-sm text-light-blue-inactive ">
                                        trocas e devoluções
                                    </Link>
                                </li>
                                <li className="my-2 list-item ">
                                    {' '}
                                    <Link
                                        href="/p/atendimento-ao-cliente"
                                        className="text-sm text-light-blue-inactive "
                                    >
                                        atendimento
                                    </Link>
                                </li>
                            </ul>
                        </div>
                        <div className="flex flex-col max-lg:items-center gap-8 justify-center w-full basis-1/4 lg:-mt-3 md:-mt-3">
                            <Image
                                src="/assets/icons/glofi_logo_completa.svg"
                                height={77}
                                width={200}
                                alt=""
                                className=""
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-footer-bg rounded-full max-sm:rounded-xl flex flex-row max-md:flex-col justify-between w-[90vw] mx-auto mt-14 -mb-8 text-center px-5 items-center p-2">
                <p className="text-sm text-secondary">© 2024 glöfi®. Todos os direitos reservados.</p>
                <div className="flex flex-row">
                    <p className="text-sm text-secondary">designed with</p>
                    <Image src={LogoCommcepta} height={50} width={100} alt="logo commcepta" className="pl-2 lg:mb-2" />
                </div>
            </div>
        </div>
    )
}
